/**
 * EshopGeoPagesList.jsx
 * ------------------------------------------------------------------
 * Renders the list of generated GEO pages with per-row preview modal
 * + copy HTML + open-on-toprankpulse links.
 *
 * Loaded inside `EshopGeoGenerator` after a job finishes — gets the
 * job_id and renders {page metadata, preview}.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Loader2, ExternalLink, Eye, Copy, Check, X, MapPin, ShoppingCart,
  Folder, FileQuestion, AlertTriangle, Globe,
} from "lucide-react";

const TYPE_ICONS = {
  product: ShoppingCart, category: Folder, faq: FileQuestion,
};

export default function EshopGeoPagesList({ jobId, userEmail, autoPublish }) {
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!jobId || !userEmail) return;
    let cancelled = false;
    axios.get(`${API}/eshop-geo/pages`, {
      headers: { "X-User-Email": userEmail },
      params: { job_id: jobId, limit: 500 },
    }).then(r => {
      if (!cancelled) setPages(r.data?.pages || []);
    }).catch(() => { if (!cancelled) setPages([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId, userEmail]);

  const handlePreview = async (page) => {
    setPreview(page); setPreviewHtml(""); setCopied(false); setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/eshop-geo/pages/${page.id}`, {
        headers: { "X-User-Email": userEmail },
      });
      setPreviewHtml(r.data?.html || "");
    } catch {
      setPreviewHtml("<!-- Preview failed -->");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCopyHtml = () => {
    if (!previewHtml) return;
    navigator.clipboard.writeText(previewHtml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Načítam pages…
      </div>
    );
  }
  if (pages.length === 0) {
    return (
      <div className="text-sm text-gray-500" data-testid="eshop-geo-pages-empty">
        Job neobsahuje žiadne pages.
      </div>
    );
  }

  return (
    <div data-testid="eshop-geo-pages-list">
      <div className="text-xs text-gray-400 mb-2">
        Vygenerovaných {pages.length} stránok. Kliknite "Náhľad" pre preview alebo "Otvoriť" pre publikovanú verziu.
      </div>
      <div className="overflow-x-auto border border-gray-900 rounded-sm">
        <table className="w-full text-xs">
          <thead className="bg-black/50 text-gray-500 uppercase tracking-widest">
            <tr>
              <th className="px-3 py-2 text-left">Typ</th>
              <th className="px-3 py-2 text-left">Mesto</th>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Slug</th>
              <th className="px-3 py-2 text-right">Akcie</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p, i) => {
              const Icon = TYPE_ICONS[p.template_type] || ShoppingCart;
              return (
                <tr key={p.id + i} className="border-t border-gray-900 hover:bg-black/30" data-testid={`eshop-geo-page-row-${i}`}>
                  <td className="px-3 py-2 text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      <Icon className="w-3 h-3" /> {p.template_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-300">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-gray-500" />
                      {p.city?.name} <span className="text-gray-500">({p.city?.country})</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-200 max-w-[280px]">
                    <span className="line-clamp-1" title={p.envelope?.title}>
                      {p.envelope?.title || "—"}
                    </span>
                    {p.used_fallback && (
                      <span className="inline-flex items-center gap-1 ml-2 text-[9px] text-amber-400" title="LLM zlyhal, použitá fallback šablóna">
                        <AlertTriangle className="w-2.5 h-2.5" /> fallback
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-purple-400 font-mono text-[10px] max-w-[200px] truncate" title={p.slug}>
                    {p.slug}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => handlePreview(p)}
                      data-testid={`eshop-geo-page-preview-${i}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded-sm"
                    >
                      <Eye className="w-3 h-3" /> Náhľad
                    </button>
                    {autoPublish && p.published && (
                      <a
                        href={`${API}/geo-page/${p.slug}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`eshop-geo-page-open-${i}`}
                        className="inline-flex items-center gap-1 ml-1 px-2 py-1 text-xs bg-purple-600 hover:bg-purple-500 text-white rounded-sm"
                      >
                        <Globe className="w-3 h-3" /> Otvoriť
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Preview modal — iframe srcDoc keeps the page sandboxed so its
          inline CSS can't leak into our app chrome. */}
      {preview && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center px-4 py-8 bg-black/80 backdrop-blur-sm"
          onClick={() => setPreview(null)}
          data-testid="eshop-geo-preview-modal"
        >
          <div
            className="relative w-full max-w-5xl h-[90vh] bg-gray-950 border border-purple-700/40 rounded-sm shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-purple-400">Náhľad stránky</div>
                <div className="text-sm font-bold text-white truncate">{preview.envelope?.title}</div>
                <div className="text-[10px] text-gray-500 truncate">/api/geo-page/{preview.slug}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={handleCopyHtml}
                  disabled={!previewHtml}
                  data-testid="eshop-geo-preview-copy-btn"
                  className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white rounded-sm inline-flex items-center gap-1"
                >
                  {copied ? <><Check className="w-3 h-3" /> Skopírované</> : <><Copy className="w-3 h-3" /> Skopírovať HTML</>}
                </button>
                {autoPublish && preview.published && (
                  <a
                    href={`${API}/geo-page/${preview.slug}`}
                    target="_blank" rel="noreferrer"
                    className="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded-sm inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Otvoriť
                  </a>
                )}
                <button
                  onClick={() => setPreview(null)}
                  data-testid="eshop-geo-preview-close"
                  className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-sm"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-hidden bg-white">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                <iframe
                  title="Page preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full h-full border-0"
                  data-testid="eshop-geo-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
